import { AfterViewInit, Component, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import * as d3 from 'd3';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { ISchool } from 'Src/ng2/shared/typings/interfaces/school.interface';
import { IStudent } from './../../store/reducers/student-reducer';

interface IGraphConst {
  key: string;
  getTodayData: Function;
  data: Store<{ loading: boolean; loaded: boolean; data: any }>;
  isCalculatedState: boolean;
  calculateGraphState: Function;
}

interface IGraphPoint {
  x: string;
  y: number;
}

/* istanbul ignore next */
@Component({
  selector: 'nvps-graph',
  template: require('./graph.component.html'),
  styles: [require('./graph.scss')],
})
export class GraphComponent implements AfterViewInit {
  // store slice for specific tile/graph
  public graphState$: Store<{ loading: boolean; loaded: boolean; data: any }>;
  public graphLoadingStatus: boolean;
  private destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() graphData: IGraphConst;
  @Input() students$: Observable<IStudent>;
  @Input() currentSchool: ISchool;

  ngOnInit() {
    this.graphState$ = this.graphData.data;
  }

  ngAfterViewInit(): void {
    this.graphState$
      .pipe(
        tap((graphState: any) => {
          const { loading, data, loaded } = graphState;
          this.graphLoadingStatus = loading;
          if (loaded && !this.graphData.isCalculatedState)
            this.generateGraph({ ...this.graphData, data }, this.students$);
          // calculated clientside
          if (this.graphData.isCalculatedState) {
            const { data } = this.graphData.calculateGraphState(this.students$);
            this.generateCountDisplay({ ...this.graphData, data });
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  private generateGraph(
    graphData: { key: string; getTodayData: Function; data: IGraphPoint[] },
    students$: Observable<IStudent>,
  ) {
    const { data, key: graphKey } = graphData;
    if (!data) return;

    // utility functions
    const parseTime: Function = d3.timeParse('%Y-%m-%d');
    const formatTime: Function = d3.timeFormat('%b %d');

    const bisectDate = d3.bisector((d: any) => d.x).left;

    const todaysData: IGraphPoint = graphData.getTodayData(students$);

    // we do not append a today calculation since we are a day behind in terms of when attendance data is available
    if (graphKey !== 'DAILY_ATT') data.push(todaysData);

    const parsedData = data.map(({ x, y }: IGraphPoint) => ({ x: parseTime(x), y }));

    // remove old graph, if it exists, we don't want to append a graph on top of another graph
    d3.select('#' + graphKey)
      .select('svg')
      .remove();
    // generate new
    const svg = d3
      .select('#' + graphKey)
      .append('svg')
      .append('g')
      .attr('transform', 'translate(' + 0 + ',' + 0 + ')');

    const graphEle = document.getElementById(graphKey);

    const graphDimension = graphEle.querySelector('svg').getBoundingClientRect();

    const width = graphDimension.width;
    // subtract 5 to give the 0 y-values a bit a room and not get cut off
    const height = graphDimension.height - 5;

    const xScale = d3.scaleTime().range([0, width]);
    const yScale = d3.scaleLinear().range([height, 0]);

    xScale.domain(d3.extent(parsedData, d => d.x));
    yScale.domain([0, d3.max(parsedData, d => d.y)]);

    // create line
    const line: any = d3
      .line()
      .x((d: any) => xScale(d.x))
      .y((d: any) => yScale(d.y))
      .curve(d3.curveMonotoneX);

    const area: any = d3
      .area()
      .x((d: any) => xScale(d.x))
      // add the 5 we subtracted in the comment above to confirm fill still fills view
      .y0(height + 5)
      .y1((d: any) => yScale(d.y))
      .curve(d3.curveMonotoneX);

    // add line path
    const linePath = svg
      .append('path')
      .data([parsedData])
      .attr('class', 'line')
      .attr('d', line);

    // add the area
    svg
      .append('path')
      .data([parsedData])
      .attr('class', 'area')
      .attr('d', area);

    const graphContainer = svg
      .append('g')
      .attr('class', 'focus')
      .style('display', 'none');

    graphContainer.append('circle').attr('r', 4.5);

    // main text shown on hover
    graphContainer
      .append('text')
      .attr('x', 9)
      .attr('dy', '.35em');

    // sub-text shown on hover (controlled by the class)
    graphContainer.append('text').attr('class', 'info');

    // vertical line on hover
    graphContainer.append('line');

    // circle at the end of the path line
    const dot = svg
      .selectAll('dot')
      .data(parsedData)
      .enter()
      .append('circle')
      .filter((d, i) => d.y !== null && i === parsedData.length - 1)
      .attr('fill', '#0a4ace')
      .attr('r', 5)
      .attr('class', 'circle')
      .attr('cx', d => xScale(d.x))
      .attr('cy', d => yScale(d.y));

    // text next to the circle at the end of the path line
    const dotText = svg
      .selectAll('text')
      .data(parsedData)
      .enter()
      .append('text')
      .filter((d, i) => d.y !== null && i === parsedData.length - 1)
      .text(d => {
        let result;
        if (graphKey === 'DAILY_ATT') result = `${d.y}%`;
        else result = d.y;
        return result;
      })
      .attr('x', d => xScale(d.x))
      .attr('y', d => yScale(d.y));

    // box that covers the plotted graph area
    svg
      .append('rect')
      .attr('class', 'overlay')
      .attr('width', width)
      .attr('height', height)
      .on('mouseout', mouseout)
      .on('mousemove', mousemove);

    function mouseout(): void {
      graphContainer.style('display', 'none');
      linePath.classed('faded', false);
      dot.classed('hidden', false);
      dotText.classed('faded', false);
    }

    function mousemove(): void {
      linePath.classed('faded', true);
      dot.classed('hidden', true);
      dotText.classed('faded', true);

      const x0: any = xScale.invert(d3.mouse(this)[0]);
      const i = bisectDate(parsedData, x0, 1);
      const d0: any = parsedData[i - 1];
      const d1: any = parsedData[i];

      // if a user is not hovering over a data point there is no data to show, simply return
      if (!d1 || !d0) return;

      // this eliminate the tooltip from the last point
      const hoveredDate = new Date(d1.x);
      const todaysDate = new Date(todaysData.x);

      /**
       * we don't want a hover-effect on the last point
       * the last point differs when it comes to daily_att
       * It is yesterday
       * we run this check to avoid the hover on the last dot
       */
      if (graphKey === 'DAILY_ATT') {
        const hoveredDateIsYesterday: boolean =
          moment(hoveredDate).format('MM/DD/YYYY') ===
          moment(todaysDate)
            .add(-1, 'days')
            .format('MM/DD/YYYY');
        if (hoveredDateIsYesterday) return;
      }

      if (graphKey !== 'DAILY_ATT') {
        const hoveredDateIsToday: boolean =
          moment(hoveredDate).format('MM/DD/YYYY') === moment(todaysDate).format('MM/DD/YYYY');
        if (hoveredDateIsToday) return;
      }

      const d = x0 - d0.x > d1.x - x0 ? d1 : d0;

      let hoverText;
      // we append a % on depending on the graphKey
      // we should make this more robust.
      // eventually many graph will have different hover displays
      if (graphKey === 'DAILY_ATT') hoverText = `${d.y}%`;
      else hoverText = d.y;

      const hoverInfo = formatTime(d.x);

      graphContainer.style('display', null);
      graphContainer.attr('transform', 'translate(' + xScale(d.x) + ',' + yScale(d.y) + ')');

      graphContainer
        .select('line')
        .attr('x1', 0)
        .attr('x2', 0)
        .attr('y1', 0)
        .attr('y2', height - yScale(d.y) + 5);

      graphContainer.select('text').text(hoverText);
      graphContainer.select('text.info').text(hoverInfo);
    }
  }

  generateCountDisplay({ key, data }) {
    // remove old graph, if it exists, we don't want to append a graph on top of another graph
    d3.select('#' + key)
      .select('span')
      .remove();
    d3.select('#' + key)
      // append count data
      .append('span')
      .text(data)
      .style('font-size', 'var(--font-xxxl)')
      .style('padding-bottom', '20px')
      .style('text-align', 'right')
      .style('font-weight', '300')
      .style('line-height', 'var(--font-xl);');
  }
}
